import { PureComponent } from 'react';
import { closeIcon, refreshIcon } from '../common/Icons';
import Hotkeys from 'react-hot-keys';
import { SlideFromRightWrapper } from '../common/SlideFromRightWrapper';
import { AntTooltip } from '../common/AntTooltip';
import moment from 'moment';
import { formattedNumber } from '../../pages/utils';
import TreatmentPaymentsTable from '../prescriptionPad/rightPanel/workspace/TreatmentPlan/TreatmentPaymentsTable';
import AutoSuggest from '../prescriptionPad/rightPanel/workspace/TreatmentPlan/AutoSuggest';
import EstimateServicesTable from './EstimateServicesTable';
import { Service, TreatmentPlan } from '../../api/getTreatmentPlans';
import { GTreatmentService } from '../../store/db';
import cloneDeep from 'lodash/cloneDeep';
import TreatmentPricing from '../prescriptionPad/rightPanel/workspace/TreatmentPlan/TreatmentPricing';
import { Data } from '../../api/ipdAdmitPatientsList';
import { ONE_CHAR_GENDER } from '../../pages/NewPatient';
import { getAgeString } from '../../store/utils';
import getIpdAdmissionEstimate, { AdmissionEstimate } from '../../api/getIpdAdmissionEstimate';
import addIpdAdmissionEstimate from '../../api/addIpdAdmissionEstimate';
import { Receipt } from '../../api/getTreatmentPlan';
import { EKA_EVENT, dispatch as ekaEventDispatch } from '../../bridge/events/dispatch';
import { Events, ON_CLOSE_BILLING_POPUP } from '../../bridge/events/types';
import { getDoc, getSelectedClinicId } from '../../store/idbHandler';

export interface StateService extends Service {
    is_new?: boolean;
}

interface Props {
    closeMe: () => void;
    item: Data;
}

interface State {
    activeTab: 'bills' | 'payments';
    lastUpdated: string;
    services: StateService[];
    receipts?: Receipt[];
    totalPaid?: number;
    totalBilled?: number;
}

type Option = GTreatmentService & { type?: string; services?: GTreatmentService[] };

class IpdBilling extends PureComponent<Props, State> {
    state: State = {
        activeTab: 'bills',
        lastUpdated: new Date().toISOString(),
        services: [],
    };

    closeIcon = closeIcon('w-12 h-12 fill-darwin-neutral-1000');
    refreshIcon = refreshIcon('w-12 h-12 fill-darwin-accent-bright-blue-800');

    componentDidMount(): void {
        const { fetchIpdBilling } = this;
        fetchIpdBilling();
        window.addEventListener(EKA_EVENT, this.onEkaEvents);
    }

    componentWillUnmount = (): void => {
        const { onEkaEvents } = this;
        window.removeEventListener(EKA_EVENT, onEkaEvents);
    };

    onEkaEvents: EventListener = (e: Event): void => {
        const { fetchIpdBilling } = this;
        const { detail } = e as CustomEvent<Events>;
        switch (detail.type) {
            case ON_CLOSE_BILLING_POPUP:
                fetchIpdBilling();
                break;
        }
    };

    fetchIpdBilling = async () => {
        const { item } = this.props;
        const { formatServices } = this;
        const { id } = item;
        const { data } = await getIpdAdmissionEstimate(id);
        this.setState({
            services: formatServices(data.items),
            totalBilled: data.totalBilled,
            totalPaid: data.totalPaid,
            receipts: data.receipts,
        });
    };

    onSelect = (option: Option) => {
        const { services } = this.state;
        const optionType = option.type;
        if (optionType === 'service') {
            services.push({
                id: option.service_id,
                name: option.name,
                teeth_ids: [],
                surfaces: [],
                qty: 1,
                mrp: option.price,
                discount: 0,
                amount: option.price,
                remarks: '',
                is_new: true,
            });
        } else {
            option.services?.forEach((service) => {
                services.push({
                    id: service.service_id,
                    name: service.name,
                    teeth_ids: [],
                    surfaces: [],
                    qty: 1,
                    mrp: service.price,
                    discount: 0,
                    amount: service.price,
                    remarks: '',
                    is_new: true,
                });
            });
        }
        this.setState({ services: cloneDeep(services) });
    };

    updateService = (newService: StateService, index: number) => {
        const { services } = this.state;
        services[index] = newService;
        this.setState({
            services: cloneDeep(services),
        });
    };

    deleteService = (index: number) => {
        const { services } = this.state;
        services.splice(index, 1);
        this.setState({
            services: cloneDeep(services),
        });
    };

    formatServices = (services: AdmissionEstimate[]): StateService[] => {
        if (!Array.isArray(services)) return [];
        return services.map((service) => ({
            id: service._id,
            name: service.name,
            qty: service.quantity,
            mrp: service.rate * service.quantity,
            discount: service.discount?.value || 0,
            amount: service.rate * service.quantity * (1 - (service.discount?.value || 0) / 100),
        }));
    };

    addToBill = async () => {
        const { item } = this.props;
        const { services } = this.state;
        const { id } = item;
        const admissionEstimates = services
            .filter((i) => i.is_new)
            .map((service) => ({
                name: service.name,
                service: service.id,
                rate: service.mrp / service.qty,
                quantity: service.qty,
                discount: {
                    category: 'percentage',
                    value: service.discount,
                },
            }));
        await addIpdAdmissionEstimate(id, admissionEstimates);
        this.fetchIpdBilling();
    };

    onAddNew = () => {
        const { item } = this.props;
        const { patient } = item;
        const { id, profile, formData, uuid } = patient;
        const { personal } = profile;
        const { name, phone } = personal;
        ekaEventDispatch({
            type: 'BILLING_POPUP',
            data: {
                docid: getDoc()?._id,
                uhid: formData?.find((fd) => fd.key === 'uhid')?.value?.toString() || '',
                patient: {
                    mobile: phone?.n,
                    uuid: uuid,
                    fln: name,
                    oid: id,
                },
                clinicId: getSelectedClinicId() || '',
                source: 'pt-details',
            },
        });
    };

    render() {
        const { closeMe, item } = this.props;
        const { lastUpdated, activeTab, services, totalBilled, totalPaid, receipts } = this.state;
        const {
            fetchIpdBilling,
            closeIcon,
            refreshIcon,
            onSelect,
            updateService,
            deleteService,
            onAddNew,
        } = this;

        const { patient } = item;
        const { profile } = patient;
        const { personal } = profile;
        const { name, gender, age } = personal;
        const ageString = getAgeString(
            {
                age: age?.value,
                month: age?.month,
                dob: age?.dob,
            },
            undefined,
            '',
        );
        const ptDetails = [name, gender && ONE_CHAR_GENDER[gender.toLocaleLowerCase()], ageString]
            .filter(Boolean)
            .join(' | ');

        return (
            <Hotkeys keyName="esc" onKeyDown={closeMe}>
                <SlideFromRightWrapper
                    closeMe={closeMe}
                    cssClass="w-80p select-none"
                    style={{ maxWidth: '1000px' }}
                    modalOverlayStyle={{ zIndex: '100' }}
                >
                    <div
                        className="w-full h-full flex flex-col bg-darwin-neutrals-50 overflow-hidden text-13"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-white px-16 py-12 flex items-center justify-between border-b border-darwin-neutral_new-200">
                            <div className="flex items-center space-x-8">
                                <p className="text-20 text-darwin-neutrals-1000">IPD Billing</p>
                                {ptDetails ? (
                                    <div className="flex items-center space-x-4 text-darwin-neutral_new-800 relative top-1">
                                        <p>for</p>
                                        <p className="font-700">{ptDetails}</p>
                                    </div>
                                ) : (
                                    ''
                                )}
                                <div className="flex relative top-2 items-center gap-4">
                                    <p className="text-neutral-5 text-13">
                                        Last updated at {moment(lastUpdated).format('hh:mm A')}
                                    </p>
                                    <AntTooltip content="Refresh">
                                        <div className="flex justify-between p-4 select-none items-center space-x-16">
                                            <div className="flex space-x-8 items-center">
                                                <button onClick={fetchIpdBilling}>
                                                    {refreshIcon}
                                                </button>
                                            </div>
                                        </div>
                                    </AntTooltip>
                                </div>
                            </div>
                            <div className="flex items-center space-x-16">
                                <button className="p-6" onClick={closeMe}>
                                    {closeIcon}
                                </button>
                            </div>
                        </div>
                        <div className="bg-white px-16 flex items-center justify-between border-b border-darwin-neutral_new-200">
                            <div>
                                <button
                                    className={`px-16 py-8 space-y-4 text-left border-b-2 ${
                                        activeTab === 'bills'
                                            ? 'text-darwin-accent-bright-blue-800 border-darwin-accent-bright-blue-800'
                                            : 'text-darwin-neutral_new-800 border-white'
                                    }`}
                                    onClick={() => {
                                        this.setState({
                                            activeTab: 'bills',
                                        });
                                    }}
                                >
                                    <p>Total Bill</p>
                                    {/* {treatmentPlanFetchState === 'loading' ? (
                                        <p className="shimmer h-24 w-64"></p>
                                    ) : ( */}
                                    <p className="min-w-64 text-16 font-700">
                                        ₹{formattedNumber(totalBilled || 0)}
                                    </p>
                                    {/* )} */}
                                </button>
                                <button
                                    className={`px-16 py-8 space-y-4 text-left border-b-2 ${
                                        activeTab === 'payments'
                                            ? 'text-darwin-accent-bright-blue-800 border-darwin-accent-bright-blue-800'
                                            : 'text-darwin-neutral_new-800 border-white'
                                    }`}
                                    onClick={() => {
                                        this.setState({
                                            activeTab: 'payments',
                                        });
                                    }}
                                >
                                    <p>Total Paid</p>
                                    {/* {treatmentPlanFetchState === 'loading' ? (
                                        <p className="shimmer h-24 w-36"></p>
                                    ) : ( */}
                                    <p className="min-w-36 text-16 font-700 text-darwin-green-primary relative -top-1">
                                        ₹{formattedNumber(totalPaid || 0)}
                                    </p>
                                    {/* )} */}
                                </button>
                            </div>
                            <div
                                className="px-16 py-12 flex items-center space-x-8 rounded-8"
                                style={{
                                    background: '#FBEDEC',
                                }}
                            >
                                <svg
                                    className="w-14 h-14 fill-red-primary"
                                    viewBox="0 0 14 14"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M7 1.84375C5.0957 1.84375 3.36914 2.85938 2.4043 4.48438C1.46484 6.13477 1.46484 8.14062 2.4043 9.76562C3.36914 11.416 5.0957 12.4062 7 12.4062C8.87891 12.4062 10.6055 11.416 11.5703 9.76562C12.5098 8.14062 12.5098 6.13477 11.5703 4.48438C10.6055 2.85938 8.87891 1.84375 7 1.84375ZM7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM7 3.875C7.33008 3.875 7.60938 4.1543 7.60938 4.48438V7.32812C7.60938 7.68359 7.33008 7.9375 7 7.9375C6.64453 7.9375 6.39062 7.68359 6.39062 7.32812V4.48438C6.39062 4.1543 6.64453 3.875 7 3.875ZM7.8125 9.5625C7.8125 10.0195 7.43164 10.375 7 10.375C6.54297 10.375 6.1875 10.0195 6.1875 9.5625C6.1875 9.13086 6.54297 8.75 7 8.75C7.43164 8.75 7.8125 9.13086 7.8125 9.5625Z" />
                                </svg>
                                <p className="text-13 text-red-primary">
                                    Total due:{' '}
                                    <span className="font-700">
                                        ₹{formattedNumber((totalBilled || 0) - (totalPaid || 0))}
                                    </span>
                                </p>
                            </div>
                        </div>
                        {activeTab === 'bills' ? (
                            <div className="flex-1 flex flex-col bg-darwin-neutrals-50">
                                <div className="p-16 flex-1 space-y-12 overflow-auto">
                                    <EstimateServicesTable
                                        services={services}
                                        updateService={updateService}
                                        deleteService={deleteService}
                                    />
                                    <AutoSuggest
                                        onSelect={onSelect}
                                        services={[]}
                                        style={{
                                            borderRadius: '0px',
                                        }}
                                        clinicId={getSelectedClinicId() || ''}
                                    />
                                </div>
                                <div className="p-16">
                                    <TreatmentPricing
                                        treatmentPlan={
                                            ({ services: services } as unknown) as TreatmentPlan
                                        }
                                    />
                                </div>
                                <div className="px-16 py-12 flex items-center justify-between border-t border-darwin-neutral_new-200 bg-white">
                                    <button
                                        className="font-700 text-darwin-accent-bright-blue-800"
                                        onClick={closeMe}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="primary-btn-v3 btn--small flex items-center space-x-8 px-16 py-8"
                                        onClick={this.addToBill}
                                    >
                                        <p>Save Changes</p>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="p-16 flex-1 flex flex-col overflow-auto bg-darwin-neutrals-50 space-y-8">
                                {Array.isArray(receipts) && (
                                    <TreatmentPaymentsTable receipts={receipts as any} />
                                )}
                                {/* {billingPopupData && !isCompletedTreatmentPlan && ( */}
                                <div>
                                    <button
                                        className="px-12 py-10 text-13 font-700 text-darwin-accent-bright-blue-800"
                                        onClick={onAddNew}
                                    >
                                        + Add New
                                    </button>
                                </div>
                                {/* )} */}
                            </div>
                        )}
                    </div>
                </SlideFromRightWrapper>
            </Hotkeys>
        );
    }
}

export default IpdBilling;
